import { useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { ConfirmDialog, RESTORE_NOTE } from '@/components/ui/confirm';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AlignLeft, ArrowLeft, FileText, Gavel, ListChecks, Plus, Trash2, Upload, Users, X } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import {
  MEETING_STATUS,
  MEETING_TYPE,
  isoToLocalInput,
  localInputToIso,
  meetingsKey,
  useMeeting,
  type Attendee,
  type MeetingStatus,
} from '@/lib/meetings';
import { useOpenRaid } from '@/lib/raid';
import { useTracker } from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { fmtDate, fmtDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/field';
import { Alert, Badge, Card, IconChip, Spinner } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { useOpenTask } from '@/components/tracker/TaskPanel';
import { UploadDialog } from '@/components/documents/UploadDialog';
import { MinutesExtract } from '@/components/ai/Extract';

export function MeetingDetailPage() {
  const program = useOutletContext<Program>();
  const { meetingId = '' } = useParams();
  const q = useMeeting(program.id, meetingId);
  const qc = useQueryClient();
  const toast = useToast();
  const openTask = useOpenTask();
  const openRaid = useOpenRaid();
  const [dialog, setDialog] = useState<null | 'action' | 'decision' | 'edit' | 'upload'>(null);
  const [asMinutes, setAsMinutes] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const nav = useNavigate();
  const remove = useMutation({
    mutationFn: () => api.delete(base),
    onSuccess: () => {
      toast.ok('Meeting deleted.');
      void qc.invalidateQueries({ queryKey: meetingsKey(program.id) });
      nav(`/p/${program.id}/meetings`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const base = `/api/programs/${program.id}/meetings/${meetingId}`;
  const refresh = () => void qc.invalidateQueries({ queryKey: meetingsKey(program.id) });
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(base, body),
    onSuccess: refresh,
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (q.isLoading) return <Spinner className="h-64" />;
  if (!q.data)
    return (
      <div className="mx-auto max-w-[900px] px-6 py-10">
        <Alert tone="bad" title="Meeting not found" />
      </div>
    );
  const m = q.data;

  return (
    <div className="mx-auto max-w-[1100px] px-6 py-6">
      <Link
        to={`/p/${program.id}/meetings`}
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-text"
      >
        <ArrowLeft className="size-4" /> Meetings
      </Link>
      <div className="mb-1 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold tracking-tight">{m.title}</h1>
        <Badge>{MEETING_TYPE[m.type]}</Badge>
        <Badge tone={MEETING_STATUS[m.status].tone}>{MEETING_STATUS[m.status].label}</Badge>
        {m.canManage && (
          <div className="ml-auto flex gap-2">
            {m.status === 'SCHEDULED' && (
              <Button variant="secondary" onClick={() => patch.mutate({ status: 'HELD' })}>
                Mark held
              </Button>
            )}
            <Button variant="secondary" onClick={() => setDialog('edit')}>
              Edit
            </Button>
            {!m.actionItems.length && !m.decisions.length && (
              <Button variant="ghost" onClick={() => setConfirmDelete(true)}>
                <Trash2 /> Delete
              </Button>
            )}
          </div>
        )}
      </div>
      <p className="mb-5 text-sm text-muted">{fmtDateTime(m.heldAt)}</p>
      <ConfirmDialog
        open={confirmDelete}
        title="Delete this meeting?"
        description={`For a meeting created by mistake. To record that a meeting did not happen, set its status to Cancelled instead. ${RESTORE_NOTE}`}
        onConfirm={() => remove.mutate()}
        onClose={() => setConfirmDelete(false)}
        loading={remove.isPending}
      />

      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        <div className="space-y-5">
          <Card className="p-0">
            <h2 className="flex items-center gap-2 border-b border-border px-4 py-2.5 text-sm font-semibold">
              <IconChip tone="ok">
                <ListChecks />
              </IconChip>{' '}
              Action items
              {m.canManage && (
                <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setDialog('action')}>
                  <Plus /> Add
                </Button>
              )}
            </h2>
            {!m.actionItems.length ? (
              <p className="px-4 py-3 text-sm text-muted">
                No action items. Each one becomes a task with an owner and a due date.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {m.actionItems.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      onClick={() => openTask(a.id)}
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-[13px] hover:bg-surface-2/60"
                    >
                      <span className="flex-1">{a.title}</span>
                      <span className="text-xs text-muted">{a.owner}</span>
                      <span className="w-24 text-xs text-muted">{a.dueDate ? `due ${fmtDate(a.dueDate)}` : ''}</span>
                      <Badge tone={a.status === 'DONE' ? 'ok' : 'neutral'}>
                        {a.status.replace('_', ' ').toLowerCase()}
                      </Badge>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-0">
            <h2 className="flex items-center gap-2 border-b border-border px-4 py-2.5 text-sm font-semibold">
              <IconChip tone="warn">
                <Gavel />
              </IconChip>{' '}
              Decisions
              {m.canManage && (
                <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setDialog('decision')}>
                  <Plus /> Add
                </Button>
              )}
            </h2>
            {!m.decisions.length ? (
              <p className="px-4 py-3 text-sm text-muted">
                No decisions. Each one goes to the RAID log, where the committee records the outcome.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {m.decisions.map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => openRaid(d.id)}
                      className="flex w-full items-center gap-3 px-4 py-2 text-left text-[13px] hover:bg-surface-2/60"
                    >
                      <span className="font-mono text-xs text-muted">{d.ref}</span>
                      <span className="flex-1">{d.title}</span>
                      {d.choice ? <Badge tone="ok">{d.choice}</Badge> : <Badge tone="info">open</Badge>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-4">
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <IconChip tone="neutral">
                <AlignLeft />
              </IconChip>
              Agenda
            </h2>
            {m.agenda ? (
              <div className="prose-helm text-[13.5px]">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.agenda}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-sm text-muted">No agenda recorded.</p>
            )}
          </Card>
        </div>

        <div className="space-y-5">
          <Card className="p-4">
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <IconChip>
                <FileText />
              </IconChip>
              Minutes and files
            </h2>
            {m.minutes ? (
              <Link
                to={`/p/${program.id}/documents/${m.minutes.id}`}
                className="flex items-center gap-2 text-sm text-brand-text hover:underline"
              >
                <FileText className="size-4" /> {m.minutes.title}
              </Link>
            ) : (
              <p className="text-sm text-muted">No minutes filed.</p>
            )}
            {m.documents
              .filter((d) => d.id !== m.minutes?.id)
              .map((d) => (
                <Link
                  key={d.id}
                  to={`/p/${program.id}/documents/${d.id}`}
                  className="mt-1 flex items-center gap-2 text-sm hover:underline"
                >
                  <FileText className="size-4 text-muted" /> {d.title}
                </Link>
              ))}
            {m.canManage && (
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setAsMinutes(true);
                    setDialog('upload');
                  }}
                >
                  <Upload /> {m.minutes ? 'Replace minutes' : 'File minutes'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setAsMinutes(false);
                    setDialog('upload');
                  }}
                >
                  <Upload /> Other file
                </Button>
                {m.minutes && (
                  <MinutesExtract
                    program={program}
                    meetingId={m.id}
                    minutesType={m.minutes.type}
                    onAdded={() => {
                      refresh();
                      void qc.invalidateQueries({ queryKey: ['raid', program.id] });
                    }}
                  />
                )}
              </div>
            )}
          </Card>
          <Card className="p-4">
            <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
              <IconChip tone="info">
                <Users />
              </IconChip>
              Attendees
            </h2>
            {!m.attendees.length ? (
              <p className="text-sm text-muted">None recorded.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {m.attendees.map((a, i) => (
                  <li key={i}>
                    {a.name}
                    {a.title && <span className="text-muted">, {a.title}</span>}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      {dialog === 'action' && (
        <ActionItemDialog program={program} base={base} onClose={() => setDialog(null)} onDone={refresh} />
      )}
      {dialog === 'decision' && <DecisionDialog base={base} onClose={() => setDialog(null)} onDone={refresh} />}
      {dialog === 'edit' && (
        <EditMeeting program={program} m={m} base={base} onClose={() => setDialog(null)} onDone={refresh} />
      )}
      {dialog === 'upload' && (
        <UploadDialog
          program={program}
          open
          target={{ meetingId: m.id, label: asMinutes ? `Minutes of ${m.title}` : m.title }}
          onClose={() => setDialog(null)}
          onUploaded={(r) => {
            if (asMinutes) patch.mutate({ minutesDocumentId: r.id });
            else refresh();
          }}
        />
      )}
    </div>
  );
}

function ActionItemDialog({
  program,
  base,
  onClose,
  onDone,
}: {
  program: Program;
  base: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const tracker = useTracker(program.id);
  const toast = useToast();
  const qc = useQueryClient();
  const [f, setF] = useState({ title: '', ownerId: '', dueDate: '', phaseId: '' });
  const add = useMutation({
    mutationFn: () => api.post(`${base}/action-items`, f),
    onSuccess: () => {
      onDone();
      void qc.invalidateQueries({ queryKey: ['tracker', program.id] });
      toast.ok('Action item added as a task.');
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const members = tracker.data?.members.filter((m) => m.role !== 'VENDOR') ?? [];
  return (
    <Dialog
      open
      onClose={onClose}
      title="Add an action item"
      description="It becomes a task with this owner and due date, and shows in their My work."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => add.mutate()}
            loading={add.isPending}
            disabled={!f.title.trim() || !f.ownerId || !f.dueDate || !f.phaseId}
          >
            Add
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="What needs doing" htmlFor="ai-title">
          <Input
            id="ai-title"
            value={f.title}
            maxLength={200}
            onChange={(e) => setF({ ...f, title: e.target.value })}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Owner" htmlFor="ai-owner">
            <Select id="ai-owner" value={f.ownerId} onChange={(e) => setF({ ...f, ownerId: e.target.value })}>
              <option value="">Choose</option>
              {members.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Due" htmlFor="ai-due">
            <Input
              id="ai-due"
              type="date"
              value={f.dueDate}
              onChange={(e) => setF({ ...f, dueDate: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Phase" htmlFor="ai-phase">
          <Select id="ai-phase" value={f.phaseId} onChange={(e) => setF({ ...f, phaseId: e.target.value })}>
            <option value="">Choose</option>
            {tracker.data?.phases.map((p) => (
              <option key={p.id} value={p.id}>
                {p.number}. {p.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Dialog>
  );
}

function DecisionDialog({ base, onClose, onDone }: { base: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [f, setF] = useState({ title: '', description: '', targetDate: '', options: '' });
  const add = useMutation({
    mutationFn: () =>
      api.post<{ ref: string }>(`${base}/decisions`, {
        title: f.title,
        ...(f.description ? { description: f.description } : {}),
        ...(f.targetDate ? { targetDate: f.targetDate } : {}),
        ...(f.options.trim()
          ? {
              options: f.options
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean)
                .map((label) => ({ label })),
            }
          : {}),
      }),
    onSuccess: (r) => {
      onDone();
      toast.ok(`${r.ref} added to the RAID log.`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title="Add a decision"
      description="It becomes a Decision in the RAID log; the outcome is recorded there."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => add.mutate()} loading={add.isPending} disabled={!f.title.trim()}>
            Add
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Decision needed" htmlFor="dc-title">
          <Input
            id="dc-title"
            value={f.title}
            maxLength={200}
            onChange={(e) => setF({ ...f, title: e.target.value })}
          />
        </Field>
        <Field label="Options" htmlFor="dc-opts" hint="One per line. Optional.">
          <Textarea id="dc-opts" rows={3} value={f.options} onChange={(e) => setF({ ...f, options: e.target.value })} />
        </Field>
        <Field label="Needed by" htmlFor="dc-date">
          <Input
            id="dc-date"
            type="date"
            value={f.targetDate}
            onChange={(e) => setF({ ...f, targetDate: e.target.value })}
          />
        </Field>
        <Field label="Context" htmlFor="dc-desc">
          <Textarea
            id="dc-desc"
            rows={2}
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
          />
        </Field>
      </div>
    </Dialog>
  );
}

function EditMeeting({
  program,
  m,
  base,
  onClose,
  onDone,
}: {
  program: Program;
  m: NonNullable<ReturnType<typeof useMeeting>['data']>;
  base: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const tracker = useTracker(program.id);
  const [f, setF] = useState({
    title: m.title,
    heldAt: isoToLocalInput(m.heldAt),
    status: m.status,
    agenda: m.agenda ?? '',
    attendees: m.attendees,
  });
  const [guest, setGuest] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api.patch(base, {
        title: f.title,
        heldAt: localInputToIso(f.heldAt),
        status: f.status,
        agenda: f.agenda || null,
        attendees: f.attendees,
      }),
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const addAttendee = (a: Attendee) =>
    !f.attendees.some((x) => (a.userId ? x.userId === a.userId : x.name === a.name)) &&
    setF({ ...f, attendees: [...f.attendees, a] });
  return (
    <Dialog
      open
      onClose={onClose}
      wide
      title="Edit meeting"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!f.title.trim() || !f.heldAt}>
            Save
          </Button>
        </>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-3">
          <Field label="Title" htmlFor="em-title">
            <Input id="em-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date and time" htmlFor="em-when">
              <Input
                id="em-when"
                type="datetime-local"
                value={f.heldAt}
                onChange={(e) => setF({ ...f, heldAt: e.target.value })}
              />
            </Field>
            <Field label="Status" htmlFor="em-status">
              <Select
                id="em-status"
                value={f.status}
                onChange={(e) => setF({ ...f, status: e.target.value as MeetingStatus })}
              >
                {(Object.keys(MEETING_STATUS) as MeetingStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {MEETING_STATUS[s].label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Field label="Agenda" htmlFor="em-agenda">
            <Textarea
              id="em-agenda"
              rows={6}
              value={f.agenda}
              onChange={(e) => setF({ ...f, agenda: e.target.value })}
            />
          </Field>
        </div>
        <div className="space-y-3">
          <p className="text-[13px] font-medium">Attendees</p>
          <ul className="space-y-1">
            {f.attendees.map((a, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                <span className="flex-1">{a.name}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${a.name}`}
                  onClick={() => setF({ ...f, attendees: f.attendees.filter((_, j) => j !== i) })}
                >
                  <X />
                </Button>
              </li>
            ))}
          </ul>
          <Select
            value=""
            aria-label="Add a member"
            onChange={(e) => {
              const mm = tracker.data?.members.find((x) => x.userId === e.target.value);
              if (mm) addAttendee({ name: mm.name, userId: mm.userId });
            }}
          >
            <option value="">Add a member</option>
            {tracker.data?.members.map((x) => (
              <option key={x.userId} value={x.userId}>
                {x.name}
              </option>
            ))}
          </Select>
          <div className="flex gap-2">
            <Input
              value={guest}
              onChange={(e) => setGuest(e.target.value)}
              placeholder="Guest name"
              aria-label="Guest name"
            />
            <Button
              variant="secondary"
              disabled={!guest.trim()}
              onClick={() => {
                addAttendee({ name: guest.trim() });
                setGuest('');
              }}
            >
              <Plus /> Guest
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
